import { useState } from 'react';

import { useBreakpointDown } from '@nesso/ui/hooks/use-breakpoint-down';
import { Button } from '@nesso/ui/primitives/button';

import type { TreatmentApproaches } from '@/entities/practice-member';
import { getTreatmentApproachLabels } from '@/entities/treatment-approach';
import { SettingsRow } from '@/shared/ui/settings/settings-row';

import { useNotesSetting } from '../model/use-notes-setting';
import { TreatmentApproachesDialog } from './treatment-approaches-input/treatment-approaches-dialog';
import { TreatmentApproachesPicker } from './treatment-approaches-input/treatment-approaches-picker';

const LABEL = 'Preferred treatment approaches';
const ERROR_MESSAGE = 'Unable to update treatment approaches. Try again.';

// The list appends on select, so unselecting then reselecting reorders an otherwise identical set.
const isSameSelection = (a: TreatmentApproaches, b: TreatmentApproaches) =>
    a.length === b.length && a.every((approach) => b.includes(approach));

export function TreatmentApproachesInput() {
    const { value: current, save } = useNotesSetting('treatmentApproaches', ERROR_MESSAGE);
    const isMobile = useBreakpointDown('xs');
    const [draft, setDraft] = useState<TreatmentApproaches | null>(null);

    const stopEditing = () => setDraft(null);

    const handleSave = () => {
        if (draft !== null && !isSameSelection(draft, current)) {
            save(draft);
        }
        stopEditing();
    };

    if (draft !== null && !isMobile) {
        return (
            <SettingsRow label={LABEL} action={{ label: 'Cancel', onClick: stopEditing }}>
                <TreatmentApproachesPicker draft={draft} onChange={setDraft} />
                <div>
                    <Button type="button" size="lg" onClick={handleSave}>
                        Save
                    </Button>
                </div>
            </SettingsRow>
        );
    }

    const labels = getTreatmentApproachLabels(current);
    const actionLabel = labels.length > 0 ? 'Edit' : 'Add';

    return (
        <>
            <SettingsRow
                label={LABEL}
                caption={labels.join(', ') || 'None selected'}
                action={{
                    label: actionLabel,
                    onClick: () => setDraft(current),
                    ariaLabel: `${actionLabel} preferred treatment approaches`,
                }}
            />
            {draft !== null && (
                <TreatmentApproachesDialog title={LABEL} onSave={handleSave} onCancel={stopEditing}>
                    <TreatmentApproachesPicker draft={draft} onChange={setDraft} fill />
                </TreatmentApproachesDialog>
            )}
        </>
    );
}
